import { useState, useEffect } from 'react';
import WrapperComponent from '../commonComponents/WrapperComponent';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { chatbotUrl } from '../../util/config';

const MODE_META = {
    rag_only: { label: 'RAG Only',    color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0', desc: 'Answers from knowledge base only' },
    strict:   { label: 'Strict Flow', color: '#dc2626', bg: '#fef2f2', border: '#fecaca', desc: 'Follows flow script exactly' },
    hybrid:   { label: 'Hybrid',      color: '#1565c0', bg: '#eff6ff', border: '#bfdbfe', desc: 'Flow + RAG for off-topic questions' },
};

const inp = { width: '100%', padding: '9px 12px', border: '1.5px solid #d1d5db', borderRadius: 8, fontSize: 13, color: '#111827', outline: 'none', boxSizing: 'border-box', background: '#fff', fontFamily: 'inherit' };
const lbl = { fontSize: 11, fontWeight: 700, color: '#6b7280', display: 'block', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.06em' };
const secLabel = { fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.06em' };

// ── Channel Selector ──────────────────────────────────────────────────────────
function ChannelSelector({ channel, onChange }) {
    const options = [
        { value: 'whatsapp', label: 'WhatsApp', icon: 'bi-whatsapp',  desc: 'WhatsApp Bot via Meta API' },
        { value: 'chat',     label: 'Web Chat', icon: 'bi-chat-dots', desc: 'Web chatbot widget'        },
    ];
    return (
        <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
            {options.map(({ value, label, icon, desc }) => {
                const active = channel === value;
                return (
                    <button key={value} onClick={() => onChange(value)} style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 12, padding: '14px 18px', borderRadius: 12, cursor: 'pointer', border: `2px solid ${active ? '#1565c0' : '#e5e7eb'}`, background: active ? '#eff6ff' : '#f8fafc', transition: 'all 0.15s', textAlign: 'left' }}>
                        <div style={{ width: 38, height: 38, borderRadius: 10, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: active ? '#1565c0' : '#e5e7eb' }}>
                            <i className={`bi ${icon}`} style={{ fontSize: 16, color: active ? '#fff' : '#6b7280' }}></i>
                        </div>
                        <div>
                            <p style={{ fontSize: 14, fontWeight: 700, color: active ? '#1565c0' : '#111827', margin: 0 }}>{label}</p>
                            <p style={{ fontSize: 11, color: '#6b7280', margin: 0, marginTop: 2 }}>{desc}</p>
                        </div>
                        {active && <i className="bi bi-check-circle-fill" style={{ marginLeft: 'auto', color: '#1565c0', fontSize: 16 }}></i>}
                    </button>
                );
            })}
        </div>
    );
}

// ── Agent Config Form ─────────────────────────────────────────────────────────
function AgentConfigForm({ agentId, channel, onChannelChange, onSaved }) {
    const [form, setForm]     = useState({ name: '', mode_override: 'rag_only', rag_tags: [], welcome_message: '', fallback_message: 'I am unable to help with that right now.', language: 'en', flow_id: null, is_active: true });
    const [loading, setLoading]   = useState(false);
    const [fetching, setFetching] = useState(false);
    const [tagInput, setTagInput] = useState('');
    const [flows, setFlows] = useState([]);
    const [tags,  setTags]  = useState([]);

    useEffect(() => {
        const token = localStorage.getItem('token');
        const fetchAll = async () => {
            try {
                const [flowsRes, tagsRes] = await Promise.all([
                    fetch(`${chatbotUrl}/flows?limit=100`,  { headers: { Authorization: `Bearer ${token}` } }),
                    fetch(`${chatbotUrl}/tags?limit=100`,   { headers: { Authorization: `Bearer ${token}` } }),
                ]);
                const flowsData = await flowsRes.json();
                const tagsData  = await tagsRes.json();
                setFlows(flowsData?.data?.items || flowsData?.data || []);
                setTags(tagsData?.data?.items   || tagsData?.data  || []);
            } catch (e) { /* ignored */ }
        };
        fetchAll();
    }, []);

    useEffect(() => {
        if (!agentId) return;
        const token = localStorage.getItem('token');
        const fetchConfig = async () => {
            setFetching(true);
            try {
                const res = await fetch(`${chatbotUrl}/chatbot/config/${agentId}`, { headers: { Authorization: `Bearer ${token}` } });
                if (!res.ok) return;
                const data = await res.json();
                const d = data?.data;
                if (d) {
                    setForm({ name: d.name||'', mode_override: d.mode_override||'rag_only', rag_tags: d.rag_tags||[], welcome_message: d.welcome_message||'', fallback_message: d.fallback_message||'I am unable to help with that right now.', language: d.language||'en', flow_id: d.flow_id||null, is_active: d.is_active !== false });
                    if (d.channel) onChannelChange(d.channel, true);
                }
            } catch (e) { /* ignored */ }
            finally { setFetching(false); }
        };
        fetchConfig();
    }, [agentId]);

    const addTag    = (tag) => { if (tag && !form.rag_tags.includes(tag)) setForm(p => ({ ...p, rag_tags: [...p.rag_tags, tag] })); setTagInput(''); };
    const removeTag = (tag) => setForm(p => ({ ...p, rag_tags: p.rag_tags.filter(t => t !== tag) }));
    const f         = (key) => (e) => setForm(p => ({ ...p, [key]: e.target.value }));

    const handleSave = async () => {
        if (!agentId?.trim()) { toast.error('Agent ID is required'); return; }
        setLoading(true);
        const token = localStorage.getItem('token');
        try {
            const res = await fetch(`${chatbotUrl}/chatbot/config/${agentId}`, {
                method: 'PUT',
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...form, channel }),
            });
            if (!res.ok) throw new Error('Failed to save');
            toast.success('Agent configuration saved', { autoClose: 2000 });
            onSaved?.();
        } catch (e) {
            toast.error('Failed to save configuration', { autoClose: 3000 });
        } finally { setLoading(false); }
    };

    const showFlow = form.mode_override === 'strict' || form.mode_override === 'hybrid';

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20, position: 'relative' }}>
            {fetching && (
                <div style={{ position: 'absolute', top: -20, left: 0, right: 0, height: 3, borderRadius: 2, background: '#bfdbfe', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: '40%', background: 'linear-gradient(90deg,transparent,#1565c0,transparent)', animation: 'slideLoad 1.2s ease-in-out infinite' }}/>
                </div>
            )}

            {/* Channel */}
            <section>
                <p style={secLabel}>Channel</p>
                <ChannelSelector channel={channel} onChange={(ch) => onChannelChange(ch, false)}/>
            </section>

            {/* Basic Info */}
            <section>
                <p style={secLabel}>Basic Info</p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div><label style={lbl}>Agent Name</label><input value={form.name} onChange={f('name')} placeholder="e.g. Customer Support Bot" style={inp}/></div>
                    <div>
                        <label style={lbl}>Language</label>
                        <select value={form.language} onChange={f('language')} style={inp}>
                            <option value="en">English</option>
                            <option value="hi">Hindi</option>
                            <option value="mr">Marathi</option>
                            <option value="ta">Tamil</option>
                            <option value="te">Telugu</option>
                            <option value="kn">Kannada</option>
                        </select>
                    </div>
                </div>
            </section>

            {/* Conversation Mode */}
            <section>
                <p style={secLabel}>Conversation Mode</p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
                    {Object.entries(MODE_META).map(([key, m]) => (
                        <button key={key} onClick={() => setForm(p => ({ ...p, mode_override: key }))}
                            style={{ padding: '12px 16px', borderRadius: 10, cursor: 'pointer', textAlign: 'left', border: `2px solid ${form.mode_override === key ? m.color : '#e5e7eb'}`, background: form.mode_override === key ? m.bg : '#f8fafc' }}>
                            <p style={{ fontSize: 13, fontWeight: 700, color: form.mode_override === key ? m.color : '#111827', margin: 0 }}>{m.label}</p>
                            <p style={{ fontSize: 11, color: '#6b7280', marginTop: 3, marginBottom: 0 }}>{m.desc}</p>
                        </button>
                    ))}
                </div>
            </section>

            {/* Flow Assignment */}
            {showFlow && (
                <section>
                    <p style={secLabel}>Flow Assignment</p>
                    <select value={form.flow_id || ''} onChange={e => setForm(p => ({ ...p, flow_id: e.target.value || null }))} style={inp}>
                        <option value="">— Select a flow —</option>
                        {flows.map(fl => <option key={fl.id || fl._id} value={fl.id || fl._id}>{fl.flow_name}</option>)}
                    </select>
                    {flows.length === 0 && <p style={{ fontSize: 11, color: '#9ca3af', marginTop: 6 }}>No flows found. Create one in Flow Builder first.</p>}
                </section>
            )}

            {/* RAG Tags */}
            <section>
                <p style={secLabel}>Knowledge Base Tags <span style={{ fontWeight: 400, color: '#9ca3af', textTransform: 'none' }}>— agent searches only these tags</span></p>
                {tags.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
                        {tags.map(t => {
                            const name = t.name || t.tag_name;
                            const sel  = form.rag_tags.includes(name);
                            return (
                                <button key={t.id || t._id} onClick={() => sel ? removeTag(name) : addTag(name)}
                                    style={{ padding: '4px 12px', fontSize: 11, fontWeight: 600, borderRadius: 20, cursor: 'pointer', background: sel ? '#1565c0' : '#f1f5f9', color: sel ? '#fff' : '#374151', border: `1px solid ${sel ? '#1565c0' : '#e5e7eb'}` }}>
                                    {sel ? '✓ ' : ''}{name}
                                </button>
                            );
                        })}
                    </div>
                )}
                <div style={{ display: 'flex', gap: 8 }}>
                    <input value={tagInput} onChange={e => setTagInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTag(tagInput); } }} placeholder="Type tag name and press Enter" style={{ ...inp, flex: 1 }}/>
                    <button onClick={() => addTag(tagInput)} style={{ padding: '9px 14px', background: '#1565c0', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer' }}>
                        <i className="bi bi-plus-lg"></i>
                    </button>
                </div>
                {form.rag_tags.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                        {form.rag_tags.map(tag => (
                            <div key={tag} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '3px 10px', background: '#eff6ff', borderRadius: 20, fontSize: 11 }}>
                                <span style={{ color: '#1565c0', fontWeight: 600, fontFamily: 'monospace' }}>{tag}</span>
                                <button onClick={() => removeTag(tag)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 0 }}>×</button>
                            </div>
                        ))}
                    </div>
                )}
            </section>

            {/* Messages */}
            <section>
                <p style={secLabel}>Messages</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div><label style={lbl}>Welcome Message</label><textarea value={form.welcome_message} onChange={f('welcome_message')} rows={2} placeholder="Hello! How can I help you today?" style={{ ...inp, resize: 'vertical' }}/></div>
                    <div><label style={lbl}>Fallback Message</label><textarea value={form.fallback_message} onChange={f('fallback_message')} rows={2} placeholder="I am unable to help with that right now." style={{ ...inp, resize: 'vertical' }}/></div>
                </div>
            </section>

            {/* Active toggle */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: '#f8fafc', borderRadius: 10, border: '1px solid #e5e7eb' }}>
                <div>
                    <p style={{ fontSize: 13, fontWeight: 600, color: '#111827', margin: 0 }}>Agent Active</p>
                    <p style={{ fontSize: 11, color: '#9ca3af', marginTop: 2, marginBottom: 0 }}>Inactive agents reject all requests</p>
                </div>
                <button onClick={() => setForm(p => ({ ...p, is_active: !p.is_active }))}
                    style={{ width: 44, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer', background: form.is_active ? '#1565c0' : '#d1d5db', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}>
                    <div style={{ width: 18, height: 18, borderRadius: '50%', background: '#fff', position: 'absolute', top: 3, left: form.is_active ? 23 : 3, transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }}/>
                </button>
            </div>

            {/* Save */}
            <button onClick={handleSave} disabled={loading}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '11px 0', fontSize: 13, fontWeight: 700, color: '#fff', background: loading ? '#9ca3af' : 'linear-gradient(to right, #2388e0, #1565c0)', border: 'none', borderRadius: 10, cursor: loading ? 'not-allowed' : 'pointer' }}>
                {loading
                    ? <><span style={{ width: 15, height: 15, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: 'white', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite' }}></span> Saving…</>
                    : <><i className="bi bi-save" style={{ fontSize: 14 }}></i> Save Configuration</>}
            </button>
            <style>{`@keyframes slideLoad{0%{transform:translateX(-150%)}100%{transform:translateX(400%)}} @keyframes spin{to{transform:rotate(360deg)}}`}</style>
        </div>
    );
}

// ── Widget Panel ──────────────────────────────────────────────────────────────
function WidgetPanel({ agentId }) {
    const [copied, setCopied] = useState(false);
    const baseUrl = chatbotUrl.replace('/v1', '');
    const snippet = `<!-- WhatsApp Chatbot Widget -->\n<script>\n  window.ChatbotConfig = {\n    agentId: '${agentId || 'your_agent_id'}',\n    apiKey:  'YOUR_API_KEY_HERE',\n    apiUrl:  '${baseUrl}',\n  };\n</script>\n<script src="${baseUrl}/widget/chatbot-widget.js" async></script>`;

    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <p style={secLabel}>Embed Widget</p>
                <button onClick={() => { navigator.clipboard.writeText(snippet); setCopied(true); toast.success('Copied!', { autoClose: 1500 }); setTimeout(() => setCopied(false), 3000); }}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', fontSize: 12, fontWeight: 600, color: copied ? '#16a34a' : '#1565c0', background: copied ? '#f0fdf4' : '#eff6ff', border: `1px solid ${copied ? '#bbf7d0' : '#bfdbfe'}`, borderRadius: 8, cursor: 'pointer' }}>
                    <i className={`bi ${copied ? 'bi-check-circle' : 'bi-clipboard'}`}></i>{copied ? 'Copied!' : 'Copy Code'}
                </button>
            </div>
            <pre style={{ background: '#111827', color: '#e5e7eb', padding: '14px 16px', borderRadius: 10, fontSize: 11, lineHeight: 1.6, overflow: 'auto', fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-all', margin: 0 }}>{snippet}</pre>
            <p style={{ fontSize: 11, color: '#9ca3af', marginTop: 8 }}>Paste before &lt;/body&gt;. Replace YOUR_API_KEY_HERE with a generated key.</p>
        </div>
    );
}

// ── Main Component ───────────────────────────────────────────────────────────
export default function AgentConfig() {
    const [agentId,     setAgentId]     = useState('whatsapp_agent_001');
    const [committedId, setCommittedId] = useState('whatsapp_agent_001');
    const [channel,     setChannel]     = useState('whatsapp');
    const [userPicked,  setUserPicked]  = useState(false);

    const handleChannelChange = (ch, fromDB = false) => { if (fromDB && userPicked) return; setChannel(ch); if (!fromDB) setUserPicked(true); };
    const handleCommit = (id) => { setTimeout(() => { setCommittedId(id); setUserPicked(false); }, 150); };

    return (
        <WrapperComponent>
            <div style={styles.page}>
                <div style={styles.pageHeader}>
                    <div>
                        <h5 style={styles.pageTitle}>Agent Configuration</h5>
                        <p style={styles.pageSubtitle}>Configure your WhatsApp AI agent — set mode, flow, messages and tags</p>
                    </div>
                </div>

                {/* Agent ID row */}
                <div style={styles.agentIdRow}>
                    <i className="bi bi-robot" style={{ color: '#1565c0', fontSize: 18, flexShrink: 0 }}></i>
                    <div style={{ flex: 1 }}>
                        <label style={lbl}>Agent ID</label>
                        <input value={agentId} onChange={e => setAgentId(e.target.value)}
                            onBlur={() => handleCommit(agentId)}
                            onKeyDown={e => { if (e.key === 'Enter') handleCommit(agentId); }}
                            placeholder="e.g. whatsapp_agent_001"
                            style={{ ...inp, fontFamily: 'monospace', color: '#1565c0', padding: '6px 10px' }}/>
                    </div>
                    <p style={{ fontSize: 11, color: '#9ca3af', maxWidth: 220, margin: 0 }}>Press Enter or click away to load this agent's config.</p>
                </div>

                <div style={styles.layout}>
                    {/* Left — form */}
                    <div style={styles.leftCol}>
                        <div style={styles.card}>
                            <AgentConfigForm agentId={committedId} channel={channel} onChannelChange={handleChannelChange} onSaved={() => {}}/>
                        </div>
                    </div>
                    {/* Right — widget */}
                    <div style={styles.rightCol}>
                        <div style={styles.card}>
                            <WidgetPanel agentId={committedId}/>
                        </div>
                        {/* Info card */}
                        <div style={styles.card}>
                            <p style={secLabel}>WhatsApp Notes</p>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                {[
                                    { icon: 'bi-info-circle', text: 'Agent ID must match the phone_number_id configured in your Meta webhook.' },
                                    { icon: 'bi-diagram-3',   text: 'Select Strict or Hybrid mode to enable Flow assignment.' },
                                    { icon: 'bi-tag',         text: 'Tags scope the RAG knowledge base — agent only searches documents under assigned tags.' },
                                    { icon: 'bi-clock-history', text: 'WhatsApp sessions auto-expire after 24 hours of inactivity (Meta billing window).' },
                                ].map((item, i) => (
                                    <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '10px 12px', background: '#f8fafc', borderRadius: 8, border: '1px solid #e5e7eb' }}>
                                        <i className={`bi ${item.icon}`} style={{ color: '#1565c0', fontSize: 13, marginTop: 1, flexShrink: 0 }}></i>
                                        <span style={{ fontSize: 12, color: '#374151' }}>{item.text}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
                <ToastContainer/>
            </div>
        </WrapperComponent>
    );
}

const styles = {
    page:        { display: 'flex', flexDirection: 'column', gap: 16, fontFamily: "'Segoe UI', system-ui, sans-serif" },
    pageHeader:  { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' },
    pageTitle:   { margin: 0, fontSize: 20, fontWeight: 700, color: '#111827' },
    pageSubtitle:{ margin: '2px 0 0', fontSize: 13, color: '#6b7280' },
    agentIdRow:  { display: 'flex', alignItems: 'center', gap: 12, padding: '14px 18px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' },
    layout:      { display: 'flex', gap: 16, alignItems: 'flex-start' },
    leftCol:     { flex: 2 },
    rightCol:    { flex: 1, display: 'flex', flexDirection: 'column', gap: 16, position: 'sticky', top: 16 },
    card:        { background: 'white', borderRadius: 12, border: '1px solid #e5e7eb', padding: 24, boxShadow: '0 1px 6px rgba(0,0,0,0.06)' },
};
